import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FileText } from 'lucide-react';
import { formatBytes } from '../../context/DownloadContext';
import { buildQrUrl } from '../shared/WizardComponents';
import { getCountryName } from '../../utils/countries';

// --- Print Section component (TI-parity) ---
const Section = ({ title, rows }) => (
  <div className="mb-4 print:break-inside-avoid">
    <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 print:grid print:grid-cols-2 print:gap-x-4 print:gap-y-1">
      {rows.filter(([, v]) => v).map(([label, value], i) => (
        <div key={i} className="flex flex-col sm:flex-row">
          <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{label}:</span>
          <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

// --- Yes/No badge helper (Division 7 standard) ---
const badge = (val) => {
  if (!val || val === '—') return '—';
  if (val === 'Yes') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600 text-white text-[10px] print:bg-green-600 print:text-white">{val}</span>;
  if (val === 'No') return <span className="inline-flex items-center px-2 py-0.5 rounded bg-red-600 text-white text-[10px] print:bg-red-600 print:text-white">{val}</span>;
  return val;
};

// --- Sections builder ---
const buildSections = (product) => [
  { title: 'Product Identification', rows: [
    ['Product Name', product.productNameModel], ['Manufacturer', product.manufacturerName || '—'],
    ['Country of Manufacturer', product.countryCode ? getCountryName(product.countryCode) : null],
    ['Joint Type', product.jointType || '—'], ['Location', (product.location || []).join(', ') || '—'],
  ]},
  { title: 'Movement Capability', rows: [
    ['Movement Rating', product.movementCapability?.movementRating ? `${product.movementCapability.movementRating}%` : '—'],
    ['Joint Width Min (in)', product.movementCapability?.jointWidthMin || '—'],
    ['Joint Width Max (in)', product.movementCapability?.jointWidthMax || '—'],
    ['Movement Type', product.movementCapability?.movementType || '—'],
  ]},
  { title: 'Fire & Life Safety', rows: [
    ['Fire Rating (hr)', product.fireLifeSafety?.fireRating || '—'],
    ['Tested Assembly', product.fireLifeSafety?.testedAssembly || '—'],
    ['Fire-resistive Continuity', badge(product.fireLifeSafety?.fireResistiveContinuity)],
  ]},
  { title: 'Environmental & Exposure', rows: [
    ['UV Exposure Rating', product.environmentalExposure?.uvExposureRating || '—'],
    ['Water Resistance', badge(product.environmentalExposure?.waterResistance)],
    ['Exterior/Interior Use', product.environmentalExposure?.exteriorInteriorUse || '—'],
    ['Chemical Resistance', product.environmentalExposure?.chemicalResistance || '—'],
  ]},
  { title: 'Substrate Compatibility', rows: [
    ['Adjacent Materials', (product.substrateCompatibility?.adjacentMaterials || []).join(', ') || '—'],
    ['Primer Required', badge(product.substrateCompatibility?.primerRequired)],
    ['Compatibility Confirmation', product.substrateCompatibility?.manufacturerCompatConfirmation || '—'],
  ]},
  { title: 'Installation Parameters', rows: [
    ['Installation Method', product.installationParameters?.installationMethod || '—'],
    ['Joint Preparation', product.installationParameters?.jointPreparation || '—'],
    ['Temperature Range (°F)', product.installationParameters?.temperatureRange || '—'],
    ['Backer Rod Required', badge(product.installationParameters?.backerRodRequired)],
  ]},
  { title: 'Interface Responsibility', rows: [
    ['Interfaces With', product.interfaceResponsibility?.interfacesWith || '—'],
    ['Responsibility', product.interfaceResponsibility?.responsibility || '—'],
    ...(product.interfaceResponsibility?.responsibility === 'Other' ? [['Other Trade', product.interfaceResponsibility?.otherTrade || '—']] : []),
  ]},
  { title: 'Warranty & Maintenance', rows: [
    ['Manufacturer Warranty (Years)', product.warrantyMaintenance?.manufacturerWarranty || '—'],
    ['Installer Warranty (Years)', product.warrantyMaintenance?.installerWarranty || '—'],
    ['Re-sealing Interval (Years)', product.warrantyMaintenance?.resealingInterval || '—'],
    ['Maintenance Responsibility', product.warrantyMaintenance?.maintenanceResponsibility || '—'],
  ]},
];

// --- Print View Content (used inside Dialog by Section, and standalone by Public page) ---
export const JointProtectionPrintView = ({ product, project }) => {
  if (!product) return null;
  const sections = buildSections(product);
  const qrUrl = product.qrToken ? buildQrUrl(product.qrToken) : '';

  return (
    <div className="px-10 py-8 text-[11px] leading-relaxed" id="jp-print-content-area" data-testid="jp-print-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6 pb-4 gap-3 print:break-inside-avoid">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-slate-900 print:text-black">07 90 00 – Joint Protection</h1>
          <p className="text-xs text-slate-500 mt-1 print:text-gray-600">{project?.name || ''} {project?.projectCode ? `(${project.projectCode})` : ''}</p>
        </div>
        {qrUrl && (
          <div className="flex flex-col items-center ml-4 shrink-0" data-testid="jp-print-qr-code">
            <QRCodeSVG value={qrUrl} size={80} level="L" />
            <p className="text-[9px] text-slate-400 mt-0.5 print:text-gray-500">Scan for live data</p>
          </div>
        )}
      </div>

      {/* Product image */}
      {(product.imageUrl || product.imageCloudinary?.urls?.medium) && (
        <div className="mb-4 print:break-inside-avoid">
          <img src={product.imageUrl || product.imageCloudinary?.urls?.medium} alt="" className="h-24 rounded border object-cover" />
        </div>
      )}

      {/* Sections */}
      {sections.map((section, si) => (
        <Section key={si} title={section.title} rows={section.rows} />
      ))}

      {/* Submittals */}
      {(product.submittals || []).length > 0 && (
        <div className="mb-4 print:break-inside-avoid">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {product.submittals.map((s, i) => (
              <div key={i} className="flex flex-col sm:flex-row">
                <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{s.documentType}:</span>
                <span className="text-xs text-slate-800 print:text-black break-words overflow-wrap-anywhere">{s.fileName} ({formatBytes(s.bytes)})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-8 pt-3 border-t flex justify-between text-[10px] text-slate-400 print:text-gray-500">
        <span>Generated by Specard</span>
        <span>{new Date().toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export const JointProtectionQR = ({ qrToken, size = 64 }) => {
  if (!qrToken) return null;
  return <QRCodeSVG value={buildQrUrl(qrToken)} size={size} level="L" />;
};

export const JointProtectionPrintViewInline = ({ product, project }) => {
  if (!product) return null;
  const sections = buildSections(product);
  return (
    <div className="text-[11px] leading-relaxed" data-testid="jp-print-view-inline">
      <div className="mb-4 border-b pb-3 print:break-inside-avoid">
        <h2 className="text-base font-bold text-slate-900 print:text-black">07 90 00 – Joint Protection</h2>
        {project?.name && <p className="text-xs text-slate-500 mt-0.5 print:text-gray-600">{project.name}</p>}
      </div>
      {sections.map((section, si) => (
        <Section key={si} title={section.title} rows={section.rows} />
      ))}
      {(product.submittals || []).length > 0 && (
        <div className="mb-4 print:break-inside-avoid">
          <h3 className="text-sm font-semibold text-slate-700 border-b border-slate-200 pb-1 mb-2 print:text-black">Submittals</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 print:grid print:grid-cols-2">
            {product.submittals.map((s, i) => (
              <div key={i} className="flex flex-col sm:flex-row">
                <span className="text-xs text-slate-500 sm:w-40 sm:shrink-0 print:text-gray-600">{s.documentType}:</span>
                <span className="text-xs text-slate-800 print:text-black break-words">{s.fileName}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
